if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cartDrawer = document.querySelector('cart-drawer');
        this.cart = document.querySelector('cart-notification') || this.cartDrawer;
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');
        this.soldOutMessage = this.submitButton.querySelector('.sold-out-message');
        this.loadingSpinner = this.querySelector('.loading__spinner');
        this.productSection = this.dataset.sectionId ? document.getElementById(`MainProduct-${this.dataset.sectionId}`) : null;
        if (!this.productSection && this.dataset.productId) {
          this.productSection = document.querySelector(`product-info[data-product-id="${this.dataset.productId}"]`);
        }
        this.addons = this.productSection ? this.productSection.querySelector('.product-addons') : null;

        if (this.cartDrawer) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();
        this.handleAddonMessage();
        this.setLoadingState(true);

        const formData = this.getFormData();

        const addonItems = this.getSelectedAddonItems(formData);
        const addToCartRequest =
          addonItems.length > 0 ? this.submitWithAddons(formData, addonItems) : this.submitMainProduct(formData);

        addToCartRequest.finally(() => {
          this.finalizeSubmit(evt);
        });
      }

      getFormData() {
        const formData = new FormData(this.form);

        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }

        return formData;
      }

      submitMainProduct(formData, addonFallback = false) {
        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];
        config.body = formData;

        return fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              this.handleCartErrorResponse(response, formData);
              return;
            }

            if (addonFallback) this.handleAddonMessage('Some add-ons could not be added. The main product was added.');
            this.handleSuccessfulCartResponse(response, formData);
          })
          .catch((error) => {
            if (addonFallback) {
              this.logAddonDebug('Main product fallback request failed.', error);
              this.handleErrorMessage(error);
            } else {
              console.error(error);
            }
          });
      }

      handleCartErrorResponse(response, formData) {
        publish(PUB_SUB_EVENTS.cartError, {
          source: 'product-form',
          productVariantId: formData.get('id'),
          errors: response.errors || response.description,
          message: response.message,
        });
        this.handleErrorMessage(response.description);

        if (!this.soldOutMessage) return;
        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButtonText.classList.add('hidden');
        this.soldOutMessage.classList.remove('hidden');
        this.error = true;
      }

      submitWithAddons(formData, addonItems) {
        this.handleAddonMessage();

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        config.body = JSON.stringify(this.buildAddonCartPayload(formData, addonItems));

        return fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) return this.handleAddonAddFailure(formData, response);

            this.normalizeMultiItemResponse(response, formData.get('id'));
            this.handleSuccessfulCartResponse(response, formData);
          })
          .catch((error) => {
            return this.handleAddonAddFailure(formData, error);
          });
      }

      handleAddonAddFailure(formData, error) {
        this.logAddonDebug('Product add-on request failed. Falling back to the main product only.', error);
        return this.submitMainProduct(formData, true);
      }

      handleSuccessfulCartResponse(response, formData) {
        if (!this.cart) {
          window.location = window.routes.cart_url;
          return;
        }

        const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
        if (!this.error)
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'product-form',
            productVariantId: formData.get('id'),
            cartData: response,
          }).then(() => {
            CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
          });
        this.error = false;
        const quickAddModal = this.closest('quick-add-modal');
        if (quickAddModal) {
          document.body.addEventListener(
            'modalClosed',
            () => {
              setTimeout(() => {
                CartPerformance.measure("add:paint-updated-sections", () => {
                  this.cart.renderContents(response);
                });
              });
            },
            { once: true }
          );
          quickAddModal.hide(true);
        } else {
          CartPerformance.measure("add:paint-updated-sections", () => {
            this.cart.renderContents(response);
          });
        }
      }

      finalizeSubmit(evt) {
        this.setLoadingState(false);
        if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
        if (!this.error) this.submitButton.removeAttribute('aria-disabled');

        CartPerformance.measureFromEvent("add:user-action", evt);
      }

      setLoadingState(isLoading) {
        this.submitButton.classList.toggle('loading', isLoading);
        this.loadingSpinner.classList.toggle('hidden', !isLoading);
        if (isLoading) this.submitButton.setAttribute('aria-disabled', true);
      }

      buildAddonCartPayload(formData, addonItems) {
        const payload = {
          items: [this.buildMainProductItem(formData), ...addonItems],
        };

        if (this.cart) {
          payload.sections = this.cart.getSectionsToRender().map((section) => section.id);
          payload.sections_url = window.location.pathname;
        }

        return payload;
      }

      buildMainProductItem(formData) {
        const item = {
          id: Number(formData.get('id')),
          quantity: this.getRequestedQuantity(formData),
        };
        const sellingPlan = formData.get('selling_plan');
        const properties = this.getLineItemProperties(formData);

        if (sellingPlan) item.selling_plan = sellingPlan;
        if (Object.keys(properties).length > 0) item.properties = properties;

        return item;
      }

      getSelectedAddonItems(formData) {
        if (!this.addons) return [];

        const quantity = this.getRequestedQuantity(formData);
        return Array.from(this.addons.querySelectorAll('.product-addon-card__checkbox:checked:not(:disabled)'), (input) => ({
          id: Number(input.value),
          quantity,
        })).filter((item) => item.id);
      }

      getRequestedQuantity(formData) {
        const quantity = Number(formData.get('quantity') || 1);
        return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
      }

      getLineItemProperties(formData) {
        const properties = {};

        formData.forEach((value, key) => {
          const match = key.match(/^properties\[(.*)\]$/);
          if (match && value !== '') properties[match[1]] = value;
        });

        return properties;
      }

      normalizeMultiItemResponse(response, mainVariantId) {
        if (!response.items || response.key) return;

        const mainItem = response.items.find((item) => item.variant_id.toString() === mainVariantId.toString());
        if (!mainItem) return;

        response.id = mainItem.id;
        response.key = mainItem.key;
      }

      handleAddonMessage(message = false) {
        if (!this.addons) return;

        this.addonMessageElement = this.addonMessageElement || this.addons.querySelector('.product-addons__message');
        if (!this.addonMessageElement && message) {
          this.addonMessageElement = document.createElement('p');
          this.addonMessageElement.className = 'product-addons__message';
          this.addonMessageElement.setAttribute('role', 'status');
          this.addons.append(this.addonMessageElement);
        }

        if (!this.addonMessageElement) return;
        this.addonMessageElement.textContent = message || '';
        this.addonMessageElement.hidden = !message;
      }

      logAddonDebug(message, details) {
        if (window.Shopify && window.Shopify.designMode) console.debug(message, details);
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
